import React, { useRef, useState, useEffect } from 'react';
import { ArrowRight, Check, X } from 'lucide-react';
import { Link } from 'react-router-dom';
import { howItWorksSteps } from '../data/mock';

const ComparisonCard = ({ title, isGazeal, items }) => (
  <div className={`p-6 rounded-2xl border transition-all duration-300 ${
    isGazeal 
      ? 'bg-[#59d862]/5 border-[#59d862]/30 hover:border-[#59d862]/50' 
      : 'bg-white/[0.02] border-white/10 hover:border-white/20'
  }`}>
    <div className="flex items-center gap-3 mb-5">
      <h4 className={`font-bold text-xl md:text-2xl ${isGazeal ? 'text-[#59d862]' : 'text-white'}`}>{title}</h4>
    </div>
    <ul className="space-y-3">
      {items.map((item, idx) => (
        <li key={idx} className="flex items-start gap-3">
          <div className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 ${
            isGazeal ? 'bg-[#59d862]/20' : 'bg-red-500/10'
          }`}>
            {isGazeal ? (
              <Check className="w-3 h-3 text-[#59d862]" />
            ) : (
              <X className="w-3 h-3 text-red-400" />
            )}
          </div>
          <span className="text-white/70 text-sm leading-relaxed">{item}</span>
        </li>
      ))}
    </ul>
  </div>
);

const HowItWorksSection = () => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setIsVisible(true); },
      { threshold: 0.2 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  const normalProcessItems = [
    "Wait 6+ months for commission after sale agreed",
    "1 in 3 sales fall through, losing your expected income",
    "Cash flow gaps affect business operations",
    "Unable to reinvest in marketing or growth",
    "Financial stress during slow market periods"
  ];

  const gazealAdvanceItems = [
    "Access commission within hours of sale agreed",
    "95% success rate with Gazeal Reservation Agreements",
    "Predictable cash flow you can plan around",
    "Reinvest immediately in your business growth",
    "Financial stability regardless of market conditions"
  ];

  return (
    <section id="how-it-works" ref={sectionRef} className="relative py-10 md:py-14 bg-black overflow-hidden">
      <div className="relative z-10 max-w-[1200px] mx-auto px-6 lg:px-12">
        <div className="text-center mb-8">
          <h2 className="text-2xl md:text-4xl lg:text-5xl font-bold mb-2 heading">
            How <span className="text-[#59d862]">Gazeal Advance</span> Works
          </h2>
          <p className="text-white/55 text-sm md:text-base max-w-lg mx-auto">Three simple steps from offer to payment in hours</p>
        </div>

        <div className="grid md:grid-cols-3 gap-4 mb-8">
          {howItWorksSteps.map((step, index) => {
            const isGetPaid = index === 2;
            return (
              <div
                key={step.step}
                className={`relative p-5 rounded-2xl transition-all duration-500 ${
                  isGetPaid 
                    ? 'bg-[#59d862]/10 border-2 border-[#59d862]/50 shadow-[0_0_30px_rgba(89,216,98,0.15)]' 
                    : 'bg-white/[0.02] border border-white/[0.06] hover:bg-[#004048]/20 hover:border-[#59d862]/30'
                } ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}
                style={{ transitionDelay: `${index * 150}ms` }}
              >
                <div className={`absolute -top-3 -left-3 w-8 h-8 rounded-xl flex items-center justify-center text-black font-bold text-sm ${
                  isGetPaid ? 'bg-[#59d862] shadow-[0_0_15px_rgba(89,216,98,0.5)]' : 'bg-[#59d862]'
                }`}>
                  {step.step}
                </div>
                <h3 className={`text-xl md:text-2xl font-bold mb-2 heading ${
                  isGetPaid ? 'text-[#59d862]' : 'text-white'
                }`}>
                  {step.title}
                </h3>
                <p className="text-white/55 text-sm leading-relaxed">{step.description}</p>
                {isGetPaid && (
                  <div className="absolute -inset-0.5 bg-[#59d862]/20 rounded-2xl blur-xl -z-10 animate-pulse" />
                )}
                {index < howItWorksSteps.length - 1 && (
                  <div className="hidden md:block absolute top-1/2 -right-3 transform -translate-y-1/2 z-10">
                    <ArrowRight className="w-4 h-4 text-[#59d862]/50" />
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Comparison Section */}
        <div className="grid md:grid-cols-2 gap-4">
          <ComparisonCard 
            title="Normal Process"
            isGazeal={false}
            items={normalProcessItems}
          />
          <ComparisonCard 
            title="With Gazeal Advance"
            isGazeal={true}
            items={gazealAdvanceItems}
          />
        </div>
      </div>
    </section>
  );
};

export default HowItWorksSection;
